<style scope>
	.ref-updated {
		color: #00ff00;
		background-color: black;
		background: linear-gradient(to top, #000000, #000000, #333333, #333333);
		background-size: cover;
		background-size: 100% 1px;
	}
	.row-title {
		font-weight: bold;
		font-size: 11pt;
		border-bottom: solid 3px blue;
		margin-top: 3px;
	}
	.stats {
		border-radius: 8px;
		background-color: rgb(178, 226, 238);
		border: solid 1px rgb(6, 4, 99);
		padding: 11px;
		padding-left: 30px;
		padding-right: 30px;
		font-size: 9pt;
		margin: 15px;
	}
	.stats-error {
		border-radius: 8px;
		background-color: rgb(255, 205, 210);
		border: solid 1px rgb(183, 28, 28);
		padding: 11px;
		padding-left: 30px;
		padding-right: 30px;
		margin: 15px;
		color: rgb(95, 7, 7);
		box-shadow: 0 0 10px rgba(183, 28, 28, 0.4);
		font-size: 9pt;
	}
	.fetch-error {
		font-size: 8pt;
		font-weight: bolder;
		font-style: italic;
	}
</style>

<template>
	<div :class="['row', isError ? 'stats-error' : 'stats']">
		<div class="row row-title">Stats - {{ props.ServerName }}</div>
		<div v-if="!cacheEnabled" class="row">
			<div class="col text-center">Cache is disabled on this server.</div>
		</div>
		<div v-if="cacheEnabled">
			<div class="row">
				<div class="col-9">Entry Count</div>
				<div class="col-3 text-end">
					<span
						:class="{
							'ref-updated': currentEntryCount != lastCurrentEntryCount,
						}"
						>{{ currentEntryCount }}</span
					>
				</div>
			</div>
			<div class="row">
				<div class="col">Est. Size</div>
				<div class="col text-end">
					<span>n/a</span>
				</div>
			</div>
			<div class="row">
				<div class="col">Total Hits</div>
				<div class="col text-end">
					<span :class="{ 'ref-updated': totalHits != lastTotalHits }">
						{{ totalHits }}
					</span>
				</div>
			</div>
			<div class="row">
				<div class="col">Total Misses</div>
				<div class="col text-end">
					<span :class="{ 'ref-updated': totalMisses != lastTotalMisses }">{{
						totalMisses
					}}</span>
				</div>
			</div>
			<div class="row">
				<div class="col">Server Total Mb</div>
				<div class="col text-end">
					<span
						:class="{ 'ref-updated': serverTotalMb != lastServerTotalMb }"
						>{{ serverTotalMb }}</span
					>
				</div>
			</div>
			<div class="row">
				<div class="col">Server Total Free Mb</div>
				<div class="col text-end">
					<span
						:class="{
							'ref-updated': serverTotalFreeMb != lastServerTotalFreeMb,
						}"
						>{{ serverTotalFreeMb }}</span
					>
				</div>
			</div>
			<div class="row">
				<div class="col">Process Mb</div>
				<div class="col text-end">
					<span
						:class="{ 'ref-updated': processMBytes != lastProcessMBytes }"
						>{{ processMBytes }}</span
					>
				</div>
			</div>
			<div class="row">
				<div class="col">Process Paged Mb</div>
				<div class="col text-end">
					<span
						:class="{
							'ref-updated': processPagedMBytes != lastProcessPagedMBytes,
						}"
						>{{ processPagedMBytes }}</span
					>
				</div>
			</div>
			<div class="row mt-3 font-weight-bold text-center">
				<div class="col">
					{{ statDate.toLocaleDateString() }}
					{{ statDate.toLocaleTimeString() }}
				</div>
			</div>
			<div :class="['row', { 'd-hidden': isError == false }]">
				<div class="col text-center fetch-error">
					{{ fetchError }}
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts" setup>
	import { onMounted, ref } from "vue";
	import { ICacheStatistics } from "./ICacheStatistics";
	import * as CacheStatistics from "../../lib/Api/CacheService";

	const props = withDefaults(
		defineProps<{
			ServerName: string;
			ServerHostname: string;
			UpdateInterval?: number;
		}>(),
		{
			UpdateInterval: 10000,
		}
	);

	const fetchError = ref<string>("");
	const isError = ref<boolean>(false);
	const currentEntryCount = ref<number>(0);
	const totalHits = ref<number>(0);
	const totalMisses = ref<number>(0);
	const serverTotalMb = ref<number>(0);
	const serverTotalFreeMb = ref<number>(0);
	const processMBytes = ref<number>(0);
	const processPagedMBytes = ref<number>(0);
	const cacheEnabled = ref<boolean>(true); // assume enabled until told otherwise

	const lastServerTotalMb = ref<number>(0);
	const lastServerTotalFreeMb = ref<number>(0);
	const lastProcessMBytes = ref<number>(0);
	const lastProcessPagedMBytes = ref<number>(0);
	const lastCurrentEntryCount = ref<number | undefined>();
	const lastTotalHits = ref<number>(0);
	const lastTotalMisses = ref<number>(0);
	const statDate = ref<Date>(new Date());

	onMounted(() => {
		UpdateStatistics();
		setInterval(UpdateStatistics, 1000 * 10);
	});

	async function UpdateStatistics() {
		lastCurrentEntryCount.value = currentEntryCount.value;
		lastTotalHits.value = totalHits.value;
		lastTotalMisses.value = totalMisses.value;
		lastServerTotalMb.value = serverTotalMb.value;
		lastServerTotalFreeMb.value = serverTotalFreeMb.value;
		lastProcessMBytes.value = processMBytes.value;
		lastProcessPagedMBytes.value = processPagedMBytes.value;

		await CacheStatistics.GetStatisticsRemote(props.ServerHostname)
			.then((result) => {
				if (result) {
					if (!lastCurrentEntryCount.value) {
						lastCurrentEntryCount.value = result?.currentEntryCount ?? 0;
						lastTotalHits.value = result?.totalHits ?? 0;
						lastTotalMisses.value = result?.totalMisses ?? 0;
						lastServerTotalMb.value = result?.serverTotalMb ?? 0;
						lastServerTotalFreeMb.value = result?.serverTotalFreeMb ?? 0;
						lastProcessMBytes.value = result?.processMbytes ?? 0;
						lastProcessPagedMBytes.value = result?.processPagedMbytes ?? 0;
					}
					currentEntryCount.value = result.currentEntryCount ?? -1;
					totalHits.value = result.totalHits ?? -1;
					totalMisses.value = result.totalMisses ?? -1;
					serverTotalMb.value = result.serverTotalMb ?? -1;
					serverTotalFreeMb.value = result.serverTotalFreeMb ?? -1;
					cacheEnabled.value = result.enabled ?? false;

					processMBytes.value = result?.processMbytes ?? -1;
					processPagedMBytes.value = result?.processPagedMbytes ?? -1;

					statDate.value = new Date();
					isError.value = false;
					fetchError.value = "";
				}
			})
			.catch((error) => {
				fetchError.value = error;
				isError.value = true;
			});
	}
</script>
